import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { 
  CreditCard, 
  Shield, 
  CheckCircle,
  Loader2,
  Zap,
  Calendar
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

/**
 * AddonPurchaseModal Component
 * 
 * Handles Razorpay payment flow for addon purchases.
 * Uses test keys (automatically detected) with easy switch to production.
 */
const AddonPurchaseModal = ({
  open,
  onOpenChange,
  addon,
  onPurchaseComplete
}) => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [razorpayConfig, setRazorpayConfig] = useState(null);
  const [pricing, setPricing] = useState(null);

  // Fetch Razorpay config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const config = await apiCall('/api/subscription-payments/config');
        setRazorpayConfig(config);
      } catch (error) {
        console.error('Failed to fetch payment config:', error);
      }
    };
    if (open) {
      fetchConfig();
    }
  }, [open, apiCall]);

  // Calculate pricing based on billing cycle
  useEffect(() => {
    if (addon) {
      const basePrice = addon.price || 0;
      const gstRate = 0.18;
      
      if (billingCycle === 'annual') {
        // 2 months free for annual
        const annualBase = basePrice * 10;
        const gst = Math.round(annualBase * gstRate * 100) / 100;
        setPricing({
          baseAmount: annualBase,
          gstAmount: gst,
          totalAmount: annualBase + gst,
          savings: basePrice * 2,
          perMonth: Math.round(annualBase / 12 * 100) / 100
        });
      } else {
        const gst = Math.round(basePrice * gstRate * 100) / 100;
        setPricing({
          baseAmount: basePrice,
          gstAmount: gst,
          totalAmount: basePrice + gst,
          savings: 0,
          perMonth: basePrice
        });
      }
    }
  }, [addon, billingCycle]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePurchase = async () => {
    if (!razorpayConfig || !addon) return;

    setLoading(true);
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      // Create order
      const orderResponse = await apiCall('/api/subscription-payments/addon/create-order', {
        method: 'POST',
        body: JSON.stringify({
          addon_key: addon.key,
          billing_cycle: billingCycle
        })
      });

      // Check if this is a trial activation (free)
      if (orderResponse.is_trial_activation) {
        toast({
          title: 'Add-on Activated!',
          description: orderResponse.message,
        });
        setLoading(false);
        onPurchaseComplete?.(orderResponse);
        onOpenChange(false);
        return;
      }

      if (!orderResponse.razorpay_order_id) {
        throw new Error('Failed to create payment order');
      }

      // Configure Razorpay
      const options = {
        key: razorpayConfig.key_id,
        amount: orderResponse.amount,
        currency: orderResponse.currency,
        name: 'Aniyor Marketplace',
        description: `${addon.name} - ${billingCycle === 'annual' ? 'Annual' : 'Monthly'}`,
        order_id: orderResponse.razorpay_order_id,
        handler: async function(response) {
          // Verify payment
          try {
            setLoading(true);  // Keep loading state during verification
            const verifyResponse = await apiCall('/api/subscription-payments/addon/verify-payment', {
              method: 'POST',
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                addon_key: addon.key
              })
            });

            if (verifyResponse.success) {
              toast({
                title: 'Payment Successful!',
                description: verifyResponse.message,
              });
              setLoading(false);
              onPurchaseComplete?.(verifyResponse);
              onOpenChange(false);
            } else {
              throw new Error(verifyResponse.detail || 'Payment verification failed');
            }
          } catch (verifyError) {
            setLoading(false);
            toast({
              title: 'Verification Failed',
              description: verifyError.message,
              variant: 'destructive'
            });
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: ''
        },
        theme: {
          color: razorpayConfig.theme_color || '#3B82F6'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      toast({
        title: 'Payment Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!addon) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Purchase Add-on
          </DialogTitle>
          <DialogDescription>
            {addon.name} - {addon.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Test Mode Indicator */}
          {razorpayConfig?.is_test_mode && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Shield className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-700">
                Test Mode - Use card 4111 1111 1111 1111 for testing
              </span>
            </div>
          )}

          {/* Billing Cycle Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Billing Cycle</Label>
            <RadioGroup value={billingCycle} onValueChange={setBillingCycle}>
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                <RadioGroupItem value="monthly" id="monthly" />
                <Label htmlFor="monthly" className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-500" />
                      <span>Monthly</span>
                    </div>
                    <span className="font-semibold">₹{addon.price}/mo</span>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer border-green-200 bg-green-50/50">
                <RadioGroupItem value="annual" id="annual" />
                <Label htmlFor="annual" className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-green-600" />
                      <span>Annual</span>
                      <Badge className="bg-green-100 text-green-700 text-xs">Save 2 months</Badge>
                    </div>
                    <span className="font-semibold">₹{addon.price * 10}/yr</span>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Pricing Breakdown */}
          {pricing && (
            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Base Amount</span>
                <span>₹{pricing.baseAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">GST (18%)</span>
                <span>₹{pricing.gstAmount.toLocaleString('en-IN')}</span>
              </div>
              {pricing.savings > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Your Savings</span>
                  <span>-₹{pricing.savings.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span className="text-lg">₹{pricing.totalAmount.toLocaleString('en-IN')}</span>
                </div>
                {billingCycle === 'annual' && (
                  <p className="text-xs text-slate-500 mt-1">
                    That's just ₹{pricing.perMonth.toLocaleString('en-IN')}/month
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Features */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">What you get:</p>
            <ul className="space-y-1">
              {addon.features?.map((feature, idx) => (
                <li key={feature.id || feature.name || `${feature.title || "feature"}-${idx}`} className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {feature}
                </li>
              )) || (
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {addon.description || 'Extended capability'}
                </li>
              )}
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handlePurchase} 
            disabled={loading || !razorpayConfig}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="h-4 w-4 mr-2" />
                Pay ₹{pricing?.totalAmount?.toLocaleString('en-IN') || '...'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddonPurchaseModal;
